/**
 * A resident's audiology referrals and History v1 (provider Cindy Aragon, Isaiah Cotto, 2026-10-21/22).
 *
 * Mirrors backend/src/routes/emergeDaySheetDocuments.js. This module, ProviderDocuments.tsx and ResidentHistory.tsx
 * are the only frontend code that names these endpoints (backend providerReferralsNeverLeave.test.js holds that), so
 * nothing that prints, exports or sends a manifest can pick a referral or the history up.
 */
import { api, apiUpload } from "../../../services/api";

export type ResidentRecordAccess = "CARE" | "OFFICE" | "ADMIN" | "AUTHOR_ONLY";

export interface ReferralDocument {
  id: string;
  original_filename: string | null;
  content_type: string;
  byte_size: number;
  version: number;
  created_at: string;
  misfiled: boolean;
  uploaded_by: { name: string | null; is_you: boolean };
}

export interface ReferralPage {
  documents: ReferralDocument[];
  truncated: boolean;
  limit: number;
  access: ResidentRecordAccess;
  can_upload: boolean;
  installed: boolean;
  accept: string[];
  max_bytes: number;
}

export type HistoryEvent =
  | { kind: "VISIT"; id: string; date: string | null; at: null; provider: string | null; encounter_type: string | null; evidence_class: string; source: "COUNSELEAR" }
  | { kind: "ATD"; id: string; date: string | null; at: string; from: string | null; to: string; by: string | null; source: "EMERGE" | "MONDAY_BASELINE" }
  | { kind: "REFERRAL"; id: string; document_id: string; date: null; at: string; by: string | null; is_you: boolean; misfiled: boolean }
  | { kind: "PRIVATE_NOTE"; id: string; date: null; at: string; is_you: true };

export interface HistorySource {
  available: boolean;
  reason?: "NO_COUNSELEAR_ID" | "NOT_IN_CARE" | "NOT_INSTALLED" | "NOT_RECORDED";
  count: number;
  truncated: boolean;
}

export interface ResidentHistory {
  events: HistoryEvent[];
  limit_per_source: number;
  access: ResidentRecordAccess;
  sources: {
    visits: HistorySource;
    atd: HistorySource;
    referrals: HistorySource;
    private_notes: HistorySource;
    tag_changes: HistorySource;
  };
}

export interface RecordResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
  /** Absent with no code: the server was never reached (services/api contract). */
  status?: number;
}

const base = (facilityId: string, patientId: string) =>
  `/emerge/day-sheet/${encodeURIComponent(facilityId)}/patients/${encodeURIComponent(patientId)}`;

export const providerDocumentsApi = {
  list: async (facilityId: string, patientId: string): Promise<RecordResult<ReferralPage>> =>
    (await api.get(`${base(facilityId, patientId)}/documents`)) as RecordResult<ReferralPage>,

  /** One file in the field `file`. The same bytes again are the same document (200, deduplicated), never a second. */
  upload: async (
    facilityId: string,
    patientId: string,
    file: File,
  ): Promise<RecordResult<{ document: ReferralDocument | null; deduplicated: boolean }>> => {
    const form = new FormData();
    form.append("file", file, file.name);
    return (await apiUpload(`${base(facilityId, patientId)}/documents`, form)) as RecordResult<{
      document: ReferralDocument | null;
      deduplicated: boolean;
    }>;
  },

  /** A 300-second signed link. Every call is recorded server-side as a read. */
  link: async (
    facilityId: string,
    patientId: string,
    documentId: string,
  ): Promise<RecordResult<{ url: string; expires_in: number; content_type: string }>> =>
    (await api.get(`${base(facilityId, patientId)}/documents/${encodeURIComponent(documentId)}/url`)) as RecordResult<{
      url: string;
      expires_in: number;
      content_type: string;
    }>,

  history: async (facilityId: string, patientId: string): Promise<RecordResult<ResidentHistory>> =>
    (await api.get(`${base(facilityId, patientId)}/history`)) as RecordResult<ResidentHistory>,
};
